import {Form, useRouteLoaderData} from 'react-router'
import {useState, type ReactNode} from 'react'
import {track} from '@core/utils/analytics'
import type {loader as rootLoader} from '@root'

export function FacebookIcon({size = 18}: {size?: number}) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path
                fill="#0866FF"
                d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"
            />
        </svg>
    )
}

type FacebookSignInProps = {
    /** The surface's own provider-button class, so the button sits beside Google's as its twin. */
    className?: string
    formClassName?: string
    redirectTo?: string | null
    trigger: string
    packSlug?: string
    label?: string
    /** Draws the submit button around the icon and label, for a surface whose Google button is a
     *  component rather than a plain `<button>`. Defaults to a plain button carrying `className`. */
    renderButton?: (content: ReactNode) => ReactNode
}

/**
 * The Facebook button, drawn only when this product offers Facebook (`signIn` in the root loader).
 * A full-document POST to the start route, like Google's: the bounce leaves the site anyway, and
 * the form fields ride the same stash (`setAuthReturn`).
 */
export function FacebookSignIn({className, formClassName, redirectTo, trigger, packSlug, label = 'Continue with Facebook', renderButton}: FacebookSignInProps) {
    const offered = useRouteLoaderData<typeof rootLoader>('root')?.signIn?.facebook
    const [inflight, setInflight] = useState(false)
    if (!offered) return null
    const content = (
        <>
            <FacebookIcon />
            <span>{inflight ? 'Connecting to Facebook…' : label}</span>
        </>
    )
    return (
        <Form
            action="/auth/facebook"
            method="POST"
            className={formClassName}
            reloadDocument
            onSubmit={() => {
                setInflight(true)
                track('auth_option_selected', {method: 'facebook', trigger, packSlug})
            }}
        >
            <input type="hidden" name="trigger" value={trigger} />
            {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
            {renderButton ? renderButton(content) : <button type="submit" className={className}>{content}</button>}
        </Form>
    )
}
